export type StrategyKey =
  | 'charge' | 'discharge' | 'standby' | 'selfuse' | 'pv_charge'
  | 'pv_fill' | 'feedin' | 'charge_only' | 'feed_priority' | 'peak_shaving'
  | 'backup' | 'charge_conditioning';

export interface StrategyDef {
  label: string;
  color: string; // hex
}

export const STRATEGIES: Record<StrategyKey, StrategyDef> = {
  charge:        { label: 'Laden',                 color: '#bbf7d0' },
  // pv_charge — passief: Solis self-use absorbeert PV-surplus in batt.
  // EMS doet niets; planner-engagement='yield'.
  pv_charge:     { label: 'PV-laden (passief)',    color: '#fde68a' },
  // pv_fill — actief: EMS engageert SC TOU charge bij site_soc < soc_max
  // + PV-overschot. Sterkere kleur (warmer geel) zodat de operator
  // visueel onderscheid kan maken met passieve self-fill.
  pv_fill:       { label: 'PV-laden (EMS push)',   color: '#fbbf24' },
  discharge:     { label: 'Ontladen',              color: '#c4b5fd' },
  // feedin — de accu wordt VASTGEHOUDEN en het PV-overschot gaat rechtstreeks
  // naar het net (43110 BIT06). Fysiek het tegenovergestelde van 'selfuse',
  // waar Solis het overschot juist de accu in laat lopen — en zonder eigen
  // kleur waren die twee in de grafiek niet uit elkaar te houden, want in
  // beide gevallen staat het setpoint op nul en ligt de accu stil.
  // Verzadigd hemelsblauw: koel (naar het net) en duidelijk apart van de warme
  // laad-tinten en van het lila van ontladen.
  feedin:        { label: 'Feed-in — PV naar net', color: '#38bdf8' },
  standby:       { label: 'Standby',               color: '#d1d5db' },
  selfuse:       { label: 'Eigen verbruik',        color: '#fed7aa' },
  charge_only:   { label: 'Alleen laden',          color: '#16a34a' },
  feed_priority: { label: 'Teruglever-prioriteit', color: '#67e8f9' },
  peak_shaving:  { label: 'Piek-bescherming',      color: '#ef4444' },
  backup:        { label: 'Noodstroom',            color: '#1f2937' },
  // charge_conditioning — de accu wordt naar 100% geladen voor BMS-conditionering
  // (periodieke vollading voor SOC-kalibratie), ook als dat niet rendabel oogt.
  // Magenta/roze: de enige tint die nog vrij was naast de groene laad-tinten,
  // het geel van PV-laden, lila ontladen, blauw/cyaan voor net en rood voor
  // piek-bescherming. Zo is het meteen te zien dat dit laden een andere reden
  // heeft dan prijs of PV.
  charge_conditioning: { label: 'Laden (Accu conditionering)', color: '#ec4899' },
};

export function mapEmsModeToStrategy(mode: string, setpointW: number): StrategyKey {
  switch (mode) {
    case 'revenue_max':
      return setpointW >= 0 ? 'discharge' : 'charge';
    case 'pv_fill':
      return 'pv_fill';
    case 'self_use':
    case 'zero_export':
      return 'selfuse';
    case 'peak_shaving':
      return 'peak_shaving';
    case 'manual':
    default:
      return 'standby';
  }
}

// Filter STRATEGIES to only the keys that appear in the given bands.
// Used by legenda-render to hide swatches for modes that never occur on the
// current site/timeline. Pass undefined for backwards-compat (= show all 8).
// Preserves STRATEGIES insertion order so the legenda is stable across renders.
export function visibleStrategies(
  bands?: { strategy: StrategyKey }[],
): [StrategyKey, StrategyDef][] {
  const all = Object.entries(STRATEGIES) as [StrategyKey, StrategyDef][];
  if (bands === undefined) return all;
  const active = new Set(bands.map((b) => b.strategy));
  return all.filter(([k]) => active.has(k));
}
